import {useCallback, useMemo, useState} from 'react'
import {DateTime} from 'luxon'
import {useTranslation} from 'react-i18next'
import {useSearchParams} from 'react-router-dom'
import {formatDate} from '@common/utils'

import {DeleteServiceModal} from './DeleteServiceModal'
import {
  AttachmentDownloadLink,
  ServicesSearchContainer,
} from './ServicesPage.styled'

import {useDownloadPartnerServiceAttachment, useGetPartnerServices} from '~/api'
import {useErrorMessage} from '~/api/helpers'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {Icon} from '~/components/general/icon/Icon'
import {SimpleTable} from '~/components/general/table/SimpleTable'
import {TableCellIconButtonPopup} from '~/components/patients/common/patientsTable/PatientsTable.styled'
import {ServicesModal} from '~/components/partnerAdmin/services/ServicesModal'
import {PublicServiceUrlModal} from '~/components/partnerAdmin/services/PublicServiceUrlModal'
import {SearchBox} from '~/components/general/searchBox/SearchBox'
import {theme} from '~/styles/theme'

type RowActionMenuProps = {
  onEdit: () => void
  onDelete: () => void
  onShowPublicUrl: () => void
}

const RowActionMenu = ({
  onEdit,
  onDelete,
  onShowPublicUrl,
}: RowActionMenuProps) => {
  const {t} = useTranslation()

  const menuItems = [
    {label: t('Edit'), onClick: onEdit},
    {label: t('Delete'), onClick: onDelete},
    {label: t('Show public URL'), onClick: onShowPublicUrl},
  ]

  return <TableCellIconButtonPopup menuItems={menuItems} />
}

export const ServicesPage = () => {
  const {t} = useTranslation<string>()
  const [searchParams, setSearchParams] = useSearchParams()
  const searchTerm = searchParams.get('searchTerm') ?? undefined
  const {data, isLoading} = useGetPartnerServices(searchTerm)
  const {downloadAttachment, loadingServiceId} =
    useDownloadPartnerServiceAttachment()
  const notifyError = useErrorMessage()

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [showPublicUrlForId, setShowPublicUrlForId] = useState<string | null>(
    null,
  )

  const headerData = useMemo(
    () => ({
      columns: [
        {title: t('Name'), columnWidthPercent: 35},
        {title: t('Start'), columnWidthPercent: 12},
        {title: t('End'), columnWidthPercent: 12},
        {title: t('Code'), columnWidthPercent: 12},
        {title: t('Attachment'), columnWidthPercent: 11},
        {title: '', columnWidthPercent: 18},
      ],
    }),
    [t],
  )

  const rows = useMemo(
    () =>
      (data || []).map((item) => ({
        rowKey: item.id,
        columns: [
          item.name,
          formatDate(DateTime.DATE_SHORT, item.startDate, undefined, '–'),
          formatDate(DateTime.DATE_SHORT, item.endDate, undefined, '–'),
          item.internalCode || '',
          item.attachmentFileId ? (
            <AttachmentDownloadLink
              href="#"
              aria-disabled={loadingServiceId === item.id}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                void (async () => {
                  try {
                    await downloadAttachment(item.id)
                  } catch (err) {
                    notifyError(
                      err instanceof Error ? err : new Error(String(err)),
                    )
                  }
                })()
              }}>
              {t('Download')}
            </AttachmentDownloadLink>
          ) : (
            '–'
          ),
        ],
        actionComponent: (
          <RowActionMenu
            onEdit={() => {
              setEditingId(item.id)
              setShowModal(true)
            }}
            onDelete={() => setDeletingId(item.id)}
            onShowPublicUrl={() => setShowPublicUrlForId(item.id)}
          />
        ),
      })),
    [data, t, downloadAttachment, loadingServiceId, notifyError],
  )

  const handleSearchQueryChange = useCallback(
    (nextSearchTerm: string) => {
      setSearchParams((params) => {
        params.set('searchTerm', nextSearchTerm)
        return params
      })
    },
    [setSearchParams],
  )

  return (
    <Container flex direction="column" spacing={4}>
      <Container flex justify="space-between" align="center">
        <Typography size="lg" weight="medium">
          {t('Services')}
        </Typography>
        <Container flex align="center" spacing={2}>
          <ServicesSearchContainer>
            <SearchBox
              fillAvailable
              onClick={handleSearchQueryChange}
              initialValue={searchParams.get('searchTerm')}
              placeholder={t('Search services...')}
            />
          </ServicesSearchContainer>
          <Button
            size="sm"
            startIcon={
              <Icon name="Plus" color={theme.colors.white} size="sm" />
            }
            onClick={() => {
              setEditingId(null)
              setShowModal(true)
            }}>
            {t('Add a service')}
          </Button>
        </Container>
      </Container>

      <SimpleTable header={headerData} rows={rows} isLoading={isLoading} />

      <ServicesModal
        isVisible={showModal}
        onClose={() => setShowModal(false)}
        serviceId={editingId}
      />

      <DeleteServiceModal
        serviceId={deletingId}
        isVisible={!!deletingId}
        onClose={() => setDeletingId(null)}
      />

      <PublicServiceUrlModal
        isVisible={!!showPublicUrlForId}
        onClose={() => setShowPublicUrlForId(null)}
        serviceId={showPublicUrlForId}
      />
    </Container>
  )
}
